import { Vector3 } from "three";

// find anchor for convex and concave polygon
export function polygonInteriorAnchor(
  points: Vector3[],
  meshIndices: number[]
): Vector3 {
  const centroid = new Vector3();
  for (const p of points) centroid.add(p);
  centroid.multiplyScalar(1 / points.length);
  if (points.length < 3 || meshIndices.length < 3) return centroid;

  let best: Vector3 | undefined;
  let bestDist = Infinity;
  for (let i = 0; i + 2 < meshIndices.length; i += 3) {
    const a = points[meshIndices[i]];
    const b = points[meshIndices[i + 1]];
    const c = points[meshIndices[i + 2]];
    if (!a || !b || !c) continue;
    const q = closestPointInTriangleXY(centroid, a, b, c);
    const d = (q.x - centroid.x) ** 2 + (q.y - centroid.y) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = q;
      if (d === 0) break; // centroid is inside this triangle → done
    }
  }
  return best ?? centroid;
}

function closestPointInTriangleXY(
  p: Vector3,
  a: Vector3,
  b: Vector3,
  c: Vector3
): Vector3 {
  const v0x = c.x - a.x, v0y = c.y - a.y;
  const v1x = b.x - a.x, v1y = b.y - a.y;
  const v2x = p.x - a.x, v2y = p.y - a.y;
  const dot00 = v0x * v0x + v0y * v0y;
  const dot01 = v0x * v1x + v0y * v1y;
  const dot02 = v0x * v2x + v0y * v2y;
  const dot11 = v1x * v1x + v1y * v1y;
  const dot12 = v1x * v2x + v1y * v2y;
  const denom = dot00 * dot11 - dot01 * dot01;
  if (Math.abs(denom) < 1e-12) return a.clone(); 
  const u = (dot11 * dot02 - dot01 * dot12) / denom; 
  const v = (dot00 * dot12 - dot01 * dot02) / denom; 
  const w = 1 - u - v;

  if (u >= 0 && v >= 0 && w >= 0) {
    return new Vector3(p.x, p.y, w * a.z + v * b.z + u * c.z);
  }
  const candidates = [
    closestPointOnSegmentXY(p, a, b),
    closestPointOnSegmentXY(p, b, c),
    closestPointOnSegmentXY(p, c, a),
  ];
  let best = candidates[0];
  let bestD = Infinity;
  for (const q of candidates) {
    const d = (q.x - p.x) ** 2 + (q.y - p.y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = q;
    }
  }
  return best;
}

function closestPointOnSegmentXY(p: Vector3, a: Vector3, b: Vector3): Vector3 {
  const abx = b.x - a.x, aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  const t =
    len2 === 0
      ? 0
      : Math.min(
          1,
          Math.max(0, ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2)
        );
  return new Vector3(a.x + abx * t, a.y + aby * t, a.z + (b.z - a.z) * t);
}

// function* MeasurementIdGenerator() {
//   let count = 1;
//   while (true) {
//     yield count++;
//   }
// }

// export const generateMeasurmentId = MeasurementIdGenerator();

function* MeasurementPreviewIdGenerator() {
  let count = 1;
  while (true) {
    yield count++;
  }
}
export const generatePreviewMeasurmentId = MeasurementPreviewIdGenerator();


export function getRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}
